import { getPrimaryCategories } from '@nykaa/product-card-shared/utils';
import { Aspect } from '@nykaa/review-card-shared/types';
import { getRoundedDiscount } from 'shared/helpers/utils/price';
import {
  Variant,
  ManufactureData,
  ProductSizeData,
  BundleProductReview,
  RawBundleProductReview,
  RawProductDetailsBundle,
  BestSellerTagRawInfo,
  BestSellerTagInfo,
  GradientColorBestSeller,
  GradientColorSegment,
  EarlyAccessData,
  RawEarlyAccessData,
  ReviewRaw,
} from 'store/product/types';
import { MediaTypes } from 'shared/constants/productPage';

import {
  Offer,
  Product,
  BrandAuth,
  CategoryLevels,
  CategoryLevelsMap,
  ReviewData,
  RawReviewData,
  ManufactureRawData,
  RawMediaItem,
} from './types';

import {
  stripScriptTags,
  removeTabCharsFromString,
  removeNewLineCharsFromString,
} from './helpers';
import { contentLang } from 'shared/constants';
import { State } from 'store/types';
import {
  getCohortSaleData,
  getCohortSaleDataV2,
  getUpdatedTemplateV2,
  transformPriceRevealData,
} from 'helpers/utils/store/priceReaveal';
import { isValidArrayLength, isValidObject } from '@nykaa/utils/validations';
import { transformPriceFluctuationData } from '@nykaa/ui-kit/component/PriceFluctuationNudge/utils';
/**
 * @name getProductsCategoryLevels
 * @param categoryLevelsMap
 * @description we get a category level map from the backend and is of type
 *  CategoryLevelsMap = {12: "0", 15: "1", 194: "0", 249: "2", 1101: "1", 1127: "2", 2889: "1", 3945: "3", 5926: "0"}
 * * This Map has categories and their corresponding category Levels
 * ? For no known reason we pick the last category in the map for each level.
 */

interface CouponDetails {
  couponCode: string;
  expiry: string;
  description: string;
  couponId: number;
  couponStatus: string;
  discount: string;
  imageUrl: string;
  fundingType: string;
  label: string;
  couponStateLabel: string;
  prive: boolean;
  priveCouponType: string;
  subTitle: string;
  title: string;
  deltaCouponDTO: { deltaText: string };
  movMoqInfoDTO: {
    totalUniqueQuantity: number | null;
    minOrderValue: number | null;
    maxOrderValue: number | null;
    minOrderQuantity: number | null;
    maxOrderQuantity: number | null;
    maxUniqueQuantity: number | null;
  };
}
export interface Coupon {
  offerId: number;
  title: string;
  description: string;
  offerType: string;
  couponDetails: CouponDetails;
  viewProducts: boolean;
  future: boolean;
  deltaAmount?: number | null;
  deltaQuantity?: number | null;
  deltaTextModified?: number | null;
  expiryEpoch?: number | null;
}
const getProductsCategoryLevels = (categoryLevelsMap: CategoryLevelsMap) => {
  const transformedCategoryMap: CategoryLevels = {};
  if (!categoryLevelsMap) {
    return transformedCategoryMap;
  }

  const categories: string[] = Object.keys(categoryLevelsMap);
  categories.forEach((category: string) => {
    const level: number = categoryLevelsMap[category];
    if (!transformedCategoryMap[level] && level < 3) {
      transformedCategoryMap[level] = category;
    }
  });

  return transformedCategoryMap;
};

const transformMedia = (media: RawMediaItem[] = []) => {
  if (!media || !Array.isArray(media)) {
    return [];
  }

  const transformRegEx = /tr:.*?\//;

  return media.map((item, index) => {
    const { type, url } = item;
    if (type === MediaTypes.IMAGE) {
      return {
        url: url.replace(transformRegEx, ''),
        mediaType: type,
      };
    }
    if (type === MediaTypes.VIDEO) {
      item.mediaType = MediaTypes.YOUTUBE_VIDEO;
    }
    item.id = index;
    return item;
  });
};

const transformCarousel = (media: RawMediaItem[] = []) => {
  if (!media || !Array.isArray(media)) {
    return [];
  }

  const transformRegEx = /tr:.*?\//;

  return media.map((item, index) => {
    const { mediaType, url, webUrl, videoId } = item;
    if (mediaType === MediaTypes.IMAGE) {
      return {
        url: url.replace(transformRegEx, ''),
        mediaType,
        id: `${index}-main_image`,
      };
    }
    if (mediaType === MediaTypes.VIDEO) {
      item.url = webUrl;
      item.id = videoId;
    }

    return item;
  });
};

export const transformManufactureData = (
  manufacturers: ManufactureRawData[]
) => {
  if (!Array.isArray(manufacturers)) {
    return [];
  }
  const transformedManufacturers: ManufactureData[] = [];
  manufacturers.forEach((manufacture: ManufactureRawData) => {
    transformedManufacturers.push({
      originOfCountryName: manufacture?.country_of_origin,
      manufacturerName: manufacture?.manufacturer_name,
      manufacturerAddress: manufacture?.manufacturer_address,
      packerName: manufacture?.packer_name,
      packerAddress: manufacture?.packer_address,
      importerName: manufacture?.importer_name,
      importerAddress: manufacture?.importer_address,
      manufacturerNameLabel: manufacture?.manufacturer_name_label,
      manufacturerAddressLabel: manufacture?.manufacturer_address_label,
      packerNameLabel: manufacture?.packer_name_label,
      packerAddressLabel: manufacture?.packer_address_label,
      importerNameLabel: manufacture?.importer_name_label,
      importerAddressLabel: manufacture?.importer_address_label,
      countryOfOriginLabel: manufacture?.country_of_origin_label,
    });
  });
  return transformedManufacturers;
};
export const transformVariants = (
  variants: any = [],
  tagsAssets: any
): Variant[] => {
  if (!variants || !Array.isArray(variants)) {
    return [];
  }

  const transformedVariants: Variant[] = [];
  variants.forEach((variant) => {
    const curationTagsurl: string[] = [];
    if (variant?.curation_tags) {
      variant.curation_tags?.forEach((tag: string) => {
        if (tagsAssets[tag]) {
          curationTagsurl?.push(tagsAssets[tag]);
        }
      });
    }
    const leftTag = variant?.left_tag;
    const leftTagUrl = tagsAssets[leftTag] || '';

    const secondaryTagUrl = tagsAssets[variant?.secondary_tag] || '';
    const secondaryTagV2Url = tagsAssets[variant?.secondary_tag_v2] || '';

    const { id: childId } = variant;
    const cohortSaleTemplateV2 = getUpdatedTemplateV2(
      variant?.cohort_sale_template,
      variant?.cohort_sale_template_v2
    );
    transformedVariants.push({
      brandName: variant.brand_name || '',
      buttonText: variant.button_text,
      newButtonText: variant.button_text_new,
      childId,
      deliveryData: variant.policy_widget,
      multiMrp: variant.multi_mrp || {},
      discount: getRoundedDiscount(variant.discount),
      featured: false, // ! FIXME: confirm featured flag for variant Object
      isBestSeller: variant.is_bestseller,
      inStock: variant.gludo_stock,
      primaryCategories: getPrimaryCategories(variant.primary_categories || {}),
      media: transformMedia(variant?.media), // TODO: transform media,
      carousel: transformCarousel(variant?.carousel),
      relatedVideo: transformCarousel(variant?.related_video),
      mrp: variant.price,
      name: variant.name,
      offerPrice: variant.final_price,
      packSize: variant.pack_size,
      isMini: variant?.is_mini || false,
      productAttributes: variant.pdp_sections,
      shadeImage: variant.shade_background_url,
      quantity: variant.quantity,
      sku: variant.sku,
      slug: variant.slug,
      productType: variant.type,
      variantName: variant.option_name,
      expiry: variant.expdt,
      returnAvailable: variant.return_available,
      messageOnReturn: variant.message_on_return,
      onlyWishlistButton: !!variant.show_wishlist_button,
      imageUrl: variant.image_url,
      manufacture: transformManufactureData(variant.manufacture) || [],
      sizeData: variant.size_data || {},
      isLux: Number(variant.is_lux) || 0,
      contentLang: variant?.[contentLang] || {},
      siblings: variant?.siblings,
      prData: transformPriceRevealData(
        variant?.pr_data,
        variant?.sale_template
      ),
      isPrEnabled: variant?.pr_enabled_raw,
      isEarlyAccess: variant?.early_access,
      isFlashSale: variant?.flash_sale,
      ratingCount: variant?.rating_count,
      reviewCount: variant?.review_count,
      rating: variant?.rating,
      cohortSaleTemplate: getCohortSaleData(variant?.cohort_sale_template),
      cohortSaleTemplateV2: getCohortSaleDataV2(
        cohortSaleTemplateV2,
        variant?.assets_bucket
      ),
      productTitle: variant?.product_title,
      urgencyTemplate: transformPriceFluctuationData(variant?.urgency_template),
      social_proof_nudges: variant?.social_proof_nudges,
      primaryTags: curationTagsurl,
      leftTag: leftTagUrl,
      secondaryTag: secondaryTagUrl,
      secondaryTagV2: secondaryTagV2Url,
      primaryTagsNames: variant.curation_tags || [],
      leftTagName: variant.left_tag || '',
      secondaryTagName: variant?.secondary_tag || '',
      earlyAccessData:
        transformEarlyAccessData(variant?.early_access_data) || {},
    });
  });
  return transformedVariants;
};

export const transformProductOffers = (offers: object[] = []): Offer[] =>
  offers.map((offer: any) => ({
    id: offer.offer_id || offer.offerId,
    title: offer.title,
    description: offer.description,
    isViewProducts: offer.isViewProducts,
    expiryEpoch: offer.expiryEpoch,
    deltaAmount: offer?.deltaAmount,
    deltaQuantity: offer?.deltaQuantity,
    deltaTextModified: offer?.deltaTextModified,
    status: offer.status,
  }));

export const transformProductCoupons = (
  coupons: Coupon[],
  defaultDescForCoupons?: string
) =>
  coupons?.map((coupon: Coupon) => ({
    id: coupon.offerId,
    title: coupon.title,
    description: coupon.couponDetails.description || defaultDescForCoupons,
    couponCode: coupon.couponDetails.couponCode,
    offerId: coupon.offerId,
    couponDescription: coupon.couponDetails.description,
    offerType: coupon.offerType,
    viewProducts: coupon.viewProducts,
    future: coupon.future,
    expiry: coupon.couponDetails.expiry,
    couponId: coupon.couponDetails.couponId,
    couponStatus: coupon.couponDetails.couponStatus,
    discount: coupon.couponDetails.discount,
    imageUrl: coupon.couponDetails.imageUrl,
    fundingType: coupon.couponDetails.fundingType,
    label: coupon.couponDetails.label,
    couponStateLabel: coupon.couponDetails.couponStateLabel,
    prive: coupon.couponDetails.prive,
    priveCouponType: coupon.couponDetails.priveCouponType,
    subTitle: coupon.couponDetails.subTitle,
    couponTitle: coupon.couponDetails.title,
    deltaCouponDTO: coupon.couponDetails.deltaCouponDTO,
    deltaAmount: coupon?.deltaAmount,
    deltaQuantity: coupon?.deltaQuantity,
    deltaTextModified: coupon?.deltaTextModified,
    movMoqInfoDTO: coupon.couponDetails.movMoqInfoDTO || {},
    expiryEpoch: coupon.expiryEpoch,
  }));

const transformAndSanitizeDetails = (details = '') => {
  if (details) {
    const regexForImgTag = /<img /g;
    const regexForImgSrc = /<img.*?src="(?<imgUrl>.*?)"/g;
    const optimizedDescription = details.replace(
      regexForImgSrc,
      '<img src="$1?tr=w-400,pr-true" '
    );

    optimizedDescription.replace(regexForImgTag, '<img loading="lazy" ');
    return stripScriptTags(
      removeTabCharsFromString(
        removeNewLineCharsFromString(optimizedDescription)
      )
    );
  }
  return null;
};

const transformBrandAuth = (brandAuth: any): undefined | BrandAuth => {
  const { text, certificate_url: certificate, auth_icon: icon } = brandAuth;

  // ? Pick certificate and text are dynamic to product/brand
  return {
    text,
    icon,
    certificate,
  };
};

const transformAspects = (aspects?: ReviewRaw[]): Aspect[] => {
  if (!isValidArrayLength(aspects)) return [];

  return (
    aspects?.map((item: ReviewRaw) => ({
      id: item.id,
      sentimentValue: item.sentiment_value,
      displayText: item.display_text,
    })) || []
  );
};

export const transformTopReview = (reviews: RawReviewData[]) => {
  const updatedReviews: ReviewData[] = [];
  reviews &&
    reviews.forEach((review) => {
      updatedReviews.push({
        title: review.title,
        description: review.detail,
        name: review.nickname,
        createdOn: review.created_at,
        likeCount: review.likes,
        id: Number(review.review_id),
        variantId: Number(review.childId),
        isLikedByUser: review.isLikedByUser,
        isBuyer: review.is_buyer,
        images: review.images || [],
        profilePic: review.customerProfileUrl,
        createdOnText: review.reviewCreationText,
        rating: Number(review.value),
        isProUser: review.pro_user,
        label: review.label,
        aspects: transformAspects(review?.aspects),
      });
    });
  return updatedReviews;
};

export const transformSizeData = (sizeData: ProductSizeData) => ({
  enabled: sizeData.enabled,
  buttonText: sizeData.button_text || '',
  unitText: sizeData.unit_text || '',
  units: sizeData.units || [],
  columns: sizeData.columns || [],
});

export const transformBundleProducts = (data: RawProductDetailsBundle[]) => {
  if (!isValidArrayLength(data)) {
    return [];
  }
  return data?.map((product) => {
    const {
      product_id,
      brand_name,
      name,
      image_url,
      rating,
      review_count,
      rating_count,
      pack_size,
    } = product;
    return {
      productId: product_id,
      brandName: brand_name,
      name,
      imageUrl: image_url,
      rating,
      reviewCount: review_count,
      ratingCount: rating_count,
      packSize: pack_size,
    };
  });
};

export const transformBundleProductsDetail = (
  data: RawBundleProductReview
): BundleProductReview => {
  if (!isValidObject(data)) {
    return {
      title: '',
      subtitle: '',
      productDetails: [],
    };
  }
  const { title, subtitle, product_details } = data;
  return {
    title,
    subtitle,
    productDetails: transformBundleProducts(product_details),
  };
};
const transformGradientColor = (
  gradientColors: GradientColorBestSeller[]
): GradientColorSegment[] | null | undefined => {
  if (
    !gradientColors ||
    !Array.isArray(gradientColors) ||
    gradientColors.length === 0
  )
    return [];
  return gradientColors?.map((color) => ({
    bgPosition: color?.bg_position,
    bgColor: color?.bg_color,
    transparency: color?.transparency,
  }));
};

const transformBestSellerInfo = (
  bestSellerInfo: BestSellerTagRawInfo
): BestSellerTagInfo | null => {
  if (!isValidObject(bestSellerInfo)) return null;
  return {
    title: bestSellerInfo?.title,
    clickableText: bestSellerInfo?.clickable_text,
    gradientColor: transformGradientColor(bestSellerInfo?.gradient_color),
  };
};

export const transformEarlyAccessData = (
  earlyAccessData: RawEarlyAccessData
): EarlyAccessData | null => {
  if (!isValidObject(earlyAccessData)) {
    return null;
  }
  return {
    disablePriveProduct: earlyAccessData.disable_prive_product,
    priveText: earlyAccessData.prive_text,
    textColor: earlyAccessData.text_color,
    gradientColor: earlyAccessData.gradient_color,
  };
};

/**
 *
 *
 * @description - transforms product Data
 * This also sets a selected skuId if the one passed as param is available in the product.
 * ! If the skuId passed is not present in the product then it selects the first variant/option returned by the API
 */
export const transformProductData = (
  productData: any,
  skuId: string,
  state: State
) => {
  const tagsAssets = productData?.tags_assets || {};
  const curationTagsurl: string[] = [];
  if (productData?.curation_tags) {
    productData.curation_tags?.forEach((tag: string) => {
      if (tagsAssets[tag]) {
        curationTagsurl?.push(tagsAssets[tag]);
      }
    });
  }

  const leftTag = productData?.left_tag;
  const leftTagUrl = tagsAssets[leftTag] || '';
  const secondaryTagUrl = tagsAssets[productData?.secondary_tag] || '';
  const secondaryTagV2Url = tagsAssets[productData?.secondary_tag_v2] || '';

  const transformedProduct: Product = {
    brandName: productData.brand_name || '',
    brandAuth: transformBrandAuth(productData.brand_auth),
    buttonText: productData.button_text,
    newButtonText: productData.button_text_new,
    canSubscribe: productData.can_subscribe,
    deliveryData: productData.policy_widget,
    categoryLevels: getProductsCategoryLevels(productData.category_levels),
    description: transformAndSanitizeDetails(productData.description),
    discount: getRoundedDiscount(productData.discount),
    id: productData.id,
    isKitCombo: productData.is_kit_combo,
    inStock: productData.gludo_stock,
    isLux: Number(productData.is_lux) || 0,
    metaDescription: productData.meta_description,
    metaKeywords: productData.meta_keywords,
    metaTitle: productData.meta_title,
    mrp: productData.price,
    sellerName: productData.seller_name,
    name: productData.name,
    offerPrice: productData.final_price,
    offers: [], // TODO: transformOffers
    productAttributes: productData.pdp_sections,
    variants: transformVariants(productData.options, tagsAssets),
    parentId: productData.parent_id,
    packSize: productData.pack_size,
    multiMrp: productData.multi_mrp,
    isMini: productData?.is_mini || false,
    hasAplusTrack: productData?.aplus_track || false,
    parentMedia: transformMedia(productData.media), // TODO: transform media
    parentCarousel: transformCarousel(productData.carousel),
    parentRelatedVideo: transformCarousel(productData.related_video),
    primaryCategories: getPrimaryCategories(productData.primary_categories),
    productType: productData.type,
    quantity: productData.quantity,
    rating: productData.rating,
    ratingCount: productData.rating_count,
    formattedRatingCount: productData.rating_count_v2,
    returnMessage: productData.message_on_return,
    reviewCount: productData.review_count,
    sku: productData.sku,
    slug: productData.slug,
    topReviews: transformTopReview(productData.top_review) || [],
    variantType: productData.configurable_type,
    ingredients: transformAndSanitizeDetails(productData.ingredients),
    howToUse: transformAndSanitizeDetails(productData.use),
    manufacture: transformManufactureData(productData.manufacture) || [],
    countryOfManufacture: productData.country_of_manufacture,
    expiry: productData.expdt,
    exploreMore: productData.explore_more?.[0],
    showBestSeller: productData.show_bestseller,
    tags: productData.tags || [],
    proFlag: productData.pro_flag,
    returnAvailable: productData.return_available,
    messageOnReturn: productData.message_on_return,
    featured: productData.featured,
    onlyWishlistButton: !!productData.show_wishlist_button,
    imageUrl: productData.image_url,
    isBackorder: !!productData.is_backorder,
    displayCombo: productData.display_combo,
    sizeData: transformSizeData(productData.size_data),
    freeShipping: productData?.shipping_data?.shipping_msg,
    contentLang: productData?.[contentLang],
    defaultPid: productData.default_pid,
    brandIds: productData?.brand_ids,
    siblings: productData?.siblings,
    bundleChildSkus: productData?.product_qty_map,
    tracking_metadata: productData?.tracking_metadata,
    prData: transformPriceRevealData(
      productData?.pr_data,
      productData?.sale_template
    ),
    pdPriceReveal: state.remoteConfig?.configs?.pdPriceReveal?.isActive,
    isSearchable: !!productData?.is_searchable,
    isPrEnabled: productData?.pr_enabled_raw,
    isEarlyAccess: productData?.early_access,
    isFlashSale: productData?.flash_sale,
    cohortSaleTemplate: getCohortSaleData(productData?.cohort_sale_template),
    cohortSaleTemplateV2: getCohortSaleDataV2(
      productData?.cohort_sale_template_v2,
      productData?.assets_bucket
    ),
    productTitle: productData?.product_title,
    brandsPart: productData?.brands_part,
    bundleProductReview: transformBundleProductsDetail(
      productData?.bundle_product_review
    ),
    urgencyTemplate: transformPriceFluctuationData(
      productData?.urgency_template
    ),
    social_proof_nudges: productData?.social_proof_nudges,
    bestSellerTagInfo: transformBestSellerInfo(
      productData?.bestseller_tag_info
    ),
    primaryTags: curationTagsurl,
    leftTag: leftTagUrl,
    secondaryTag: secondaryTagUrl,
    primaryTagsNames: productData?.curation_tags || [],
    leftTagName: productData?.left_tag || '',
    secondaryTagName: productData?.secondary_tag || '',
    secondaryTagV2Name: productData?.secondary_tag_v2 || '',
    earlyAccessData:
      transformEarlyAccessData(productData?.early_access_data) || {},
    secondaryTagV2: secondaryTagV2Url,
  };
  if (state.remoteConfig?.configs?.defaultVariantConfig?.status) {
    const { ids, categoryLevels } =
      state.remoteConfig?.configs?.defaultVariantConfig?.config || {};
    const isNotDefaultSelected = categoryLevels?.find(
      (categoryLevel: string) => {
        if (
          ids?.includes(
            transformedProduct?.primaryCategories[categoryLevel]?.id
          )
        ) {
          return true;
        }
        return false;
      }
    );

    if (isNotDefaultSelected) {
      return transformedProduct;
    }
  }

  const { variants, defaultPid, id } = transformedProduct;
  let selectedVariantId = id;
  let selectedVariantName = '';
  if (variants && variants.length > 0) {
    if (skuId) {
      let skuIdIndex = variants.findIndex(
        (variant) => variant.childId === skuId
      );
      if (skuIdIndex < 0) {
        skuIdIndex = 0;
      }
      const selectedVariant = variants[skuIdIndex];
      selectedVariantId = selectedVariant.childId;
      selectedVariantName = selectedVariant.variantName;
    } else {
      const [firstVariant] = variants;
      selectedVariantId = defaultPid || firstVariant.childId;
      selectedVariantName = firstVariant.variantName;
    }
  }

  transformedProduct.selectedVariantId = selectedVariantId;
  transformedProduct.selectedVariantName = selectedVariantName;
  return transformedProduct;
};

export const transformDynamicFooterLinks = (data: any) => {
  if (!data) {
    return [];
  }
  return data.TopSearchedCategoriesList;
};

export const transformPassDetails = (data: any) => {
  if (!data) {
    return [];
  }

  return data;
};
